import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Chip,
  Link,
  Tooltip,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Refresh,
  Replay,
  Sync,
  OpenInNew,
  AccountBalanceWallet,
  EventRepeat,
  SmartToy,
  People,
  Edit,
  LockReset,
  ContentCopy,
  CheckCircle,
  DeleteForever,
  Storage as StorageIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import {
  AccountEmployee,
  ClientAccountDetail,
  CreditPlanFields,
  PublicListingOption,
  CreditEntry
} from '../types/clientAccount';
// Veredicto del tenant sobre el disco (applied/deferred/failed), leído igual
// aquí que en los portales de partner y cliente.
import { storageSyncFeedback } from '../utils/storageSync';
import EmployeeStatusChip from '../components/EmployeeStatusChip';
import AccountUserStatusChip from '../components/AccountUserStatusChip';
import PortalAccessPanel from '../components/PortalAccessPanel';
import LoadingButton from '../components/LoadingButton';
import EmployeeCatalogLink from '../components/EmployeeCatalogLink';
// Mismo selector de volúmenes (y mismo cálculo de impacto) que el portal del
// cliente y el del partner: las reglas de tamaño son las del backend.
import EmployeeSizePicker, {
  DEFAULT_EMPLOYEE_SIZE, EmployeeSize, EmployeeSizeChip
} from '../components/EmployeeSizePicker';
import EmployeeStoragePicker, {
  EmployeeVolumes,
  defaultVolumes
} from '../components/EmployeeStoragePicker';
import { formatCredits } from './ClientAccountsPage';

const toNum = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// 'YYYY-MM-DD' anclado a medianoche LOCAL (sin el T00:00:00, JS lo parsea como
// UTC y en husos negativos se pintaría el día anterior) — igual que CreditsSummary.
const formatRenewDate = (raw: string): string | null => {
  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const ClientAccountDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const accountId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [account, setAccount] = useState<ClientAccountDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Añadir créditos EXTRA (topup — bolsa separada del plan mensual)
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsConcept, setCreditsConcept] = useState('');

  // Asignar/cambiar el PLAN mensual (créditos/mes + día de ciclo 1..28)
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [planCredits, setPlanCredits] = useState('');
  const [planCycleDay, setPlanCycleDay] = useState('1');

  // Asignar empleado — picker por NOMBRE del catálogo público (rellena el slug)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [listingOptions, setListingOptions] = useState<PublicListingOption[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [selectedListingSlug, setSelectedListingSlug] = useState('');
  const [assignDisplayName, setAssignDisplayName] = useState('');
  // ADMINISTRADOR del empleado: usuario de la cuenta (obligatorio). Es también
  // la identidad con la que el empleado trabajará — el propietario de la cuenta
  // ya no vale para eso (su login es solo del portal).
  const [assignAdminUserId, setAssignAdminUserId] = useState<number | ''>('');
  // Volúmenes del empleado que se asigna. El admin también los elige: el
  // tamaño se decide al nacer (ampliarlo después es permanente y con cargo), y
  // su forma la valida el backend igual que en los otros dos caminos.
  const [assignVolumes, setAssignVolumes] = useState<EmployeeVolumes>({
    workspace_gib: 10, data_gib: 10
  });
  // TALLA (capacidad) del empleado. Se elige aquí por lo mismo que el disco:
  // se aplica al desplegarlo y cambiarla después exige redesplegar.
  const [assignSize, setAssignSize] = useState<EmployeeSize>(DEFAULT_EMPLOYEE_SIZE);

  // Almacenamiento: editar la configuración de la cuenta (incluido sin coste y
  // techo) y ampliar el disco de un empleado concreto. Dos diálogos distintos
  // porque son dos decisiones distintas: una es comercial (qué le incluyo), la
  // otra es operativa e IRREVERSIBLE (cuánto disco le doy a esta app).
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);
  const [includedValue, setIncludedValue] = useState('20');
  const [maxStorageValue, setMaxStorageValue] = useState('0');
  const [expandTarget, setExpandTarget] = useState<AccountEmployee | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [expandError, setExpandError] = useState<string | null>(null);

  // Editar cupo de empleados (max_employees) — PATCH parcial
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaValue, setQuotaValue] = useState('1');

  // Resetear contraseña del propietario: confirmación + contraseña generada
  // mostrada UNA sola vez (mismo patrón que el alta en ClientAccountsPage)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetInfo, setResetInfo] = useState<{
    email: string;
    generated_password: string;
    // El backend intenta enviarla también por correo al propietario; si no salió
    // (sin SMTP o fallo), se mantiene el texto de siempre: entrégala tú.
    email_sent?: boolean;
  } | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  // Otras acciones
  const [syncLoading, setSyncLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  // Eliminar la cuenta (borrado FÍSICO). Si quedan empleados sin retirar hace
  // falta force=true, y entonces el diálogo exige escribir el nombre exacto.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const pollTimer = useRef<number | null>(null);

  const loadAccount = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setError('');
      const data = await apiService.getClientAccount(accountId);
      setAccount(data);
    } catch (err: any) {
      console.error('Failed to load client account:', err);
      if (!silent) {
        setError(err.detail || t('clientAccounts.detailLoadError', 'No se pudo cargar la cuenta'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [accountId, t]);

  useEffect(() => {
    if (!Number.isNaN(accountId)) loadAccount();
  }, [accountId, loadAccount]);

  // Mientras haya empleados en pending/provisioning, refresco silencioso cada 5 s
  // para que el estado del despliegue avance solo (sin que el admin pulse nada).
  useEffect(() => {
    const hasInProgress = account?.employees.some(
      (e) => e.status === 'pending' || e.status === 'provisioning'
    );
    if (hasInProgress && pollTimer.current === null) {
      pollTimer.current = window.setInterval(() => loadAccount(true), 5000);
    }
    if (!hasInProgress && pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [account, loadAccount]);

  const handleAddCredits = async () => {
    const amount = Number(creditsAmount);
    if (!amount || amount <= 0) return;
    try {
      setCreditsLoading(true);
      const result = await apiService.addClientAccountCredits(accountId, {
        amount,
        concept: creditsConcept.trim() || undefined,
        kind: 'topup'
      });
      toast.success(
        t('clientAccounts.creditsAdded', 'Créditos añadidos. Saldo actual: {{balance}}', {
          balance: formatCredits(result.balance)
        })
      );
      setCreditsDialogOpen(false);
      setCreditsAmount('');
      setCreditsConcept('');
      loadAccount();
    } catch (err: any) {
      console.error('Failed to add credits:', err);
      toast.error(err.detail || t('clientAccounts.creditsError', 'No se pudieron añadir los créditos'));
    } finally {
      setCreditsLoading(false);
    }
  };

  // Plan mensual: créditos/mes > 0 y día de ciclo entero 1..28 (contrato backend).
  const planCreditsValid =
    planCredits.trim() !== '' && !Number.isNaN(Number(planCredits)) && Number(planCredits) > 0;
  const planCycleDayValid =
    /^\d+$/.test(planCycleDay.trim()) && Number(planCycleDay) >= 1 && Number(planCycleDay) <= 28;

  const openPlanDialog = () => {
    // El GET ya trae la config que edita el admin (plan_credits/plan_cycle_day);
    // los fallbacks (grant del ciclo en credit_summary, día inferido de la
    // próxima renovación) cubren backends viejos sin esos campos.
    const currentPlan =
      toNum(account?.plan_credits) ??
      toNum(account?.credit_summary?.plan_total) ??
      toNum(account?.plan_total);
    setPlanCredits(currentPlan !== null && currentPlan > 0 ? String(currentPlan) : '');
    let day = 1;
    const cycleDay = account?.plan_cycle_day;
    if (typeof cycleDay === 'number' && cycleDay >= 1 && cycleDay <= 28) {
      day = cycleDay;
    } else {
      const renewsAt = account?.credit_summary?.plan_renews_at ?? account?.plan_renews_at;
      const renewMatch = renewsAt ? String(renewsAt).match(/^\d{4}-\d{2}-(\d{2})/) : null;
      if (renewMatch) {
        const d = Number(renewMatch[1]);
        if (d >= 1 && d <= 28) day = d;
      }
    }
    setPlanCycleDay(String(day));
    setPlanDialogOpen(true);
  };

  const handleAssignPlan = async () => {
    if (!planCreditsValid || !planCycleDayValid) return;
    try {
      setPlanLoading(true);
      await apiService.setAccountPlan(accountId, {
        credits: Number(planCredits),
        cycle_day: Number(planCycleDay)
      });
      toast.success(t('clientAccounts.planAssigned', 'Plan asignado'));
      setPlanDialogOpen(false);
      loadAccount();
    } catch (err: any) {
      console.error('Failed to assign plan:', err);
      toast.error(err.detail || t('clientAccounts.planError', 'No se pudo asignar el plan'));
    } finally {
      setPlanLoading(false);
    }
  };

  // Usuarios elegibles como administrador: los de la cuenta que no estén
  // deshabilitados (un pending sirve: la provisión reintenta su alta en el tenant).
  const adminCandidates = (account?.users ?? []).filter((u) => u.status !== 'disabled');

  const openAssignDialog = async () => {
    setAssignDialogOpen(true);
    setSelectedListingSlug('');
    setAssignDisplayName('');
    // Tamaño de salida = los mínimos del backend (lo que aplicaría si no se
    // mandara nada): asignar sin tocar el selector deja el empleado de siempre.
    setAssignVolumes(defaultVolumes(storage));
    // Con un solo usuario elegible se preselecciona: es la única opción posible.
    setAssignAdminUserId(adminCandidates.length === 1 ? adminCandidates[0].id : '');
    try {
      setLoadingListings(true);
      const options = await apiService.getPublicListingOptions();
      setListingOptions(options);
    } catch (err: any) {
      console.error('Failed to load listing catalog:', err);
      toast.error(t('clientAccounts.catalogError', 'No se pudo cargar el catálogo de empleados'));
    } finally {
      setLoadingListings(false);
    }
  };

  const handleAssignEmployee = async () => {
    if (!selectedListingSlug || assignAdminUserId === '') return;
    try {
      setAssignLoading(true);
      await apiService.assignClientAccountEmployee(accountId, {
        listing_slug: selectedListingSlug,
        display_name: assignDisplayName.trim() || undefined,
        admin_account_user_id: assignAdminUserId as number,
        // Los volúmenes viajan siempre: el empleado NACE con el disco elegido
        // (el install del tenant los toma de estas columnas). Ampliarlos
        // después es permanente y con cargo, así que nacer bien sale gratis.
        workspace_gib: assignVolumes.workspace_gib,
        data_gib: assignVolumes.data_gib,
        size: assignSize
      });
      toast.success(t('clientAccounts.employeeAssigned', 'Empleado asignado: el despliegue ha comenzado'));
      setAssignDialogOpen(false);
      loadAccount();
    } catch (err: any) {
      console.error('Failed to assign employee:', err);
      toast.error(err.detail || t('clientAccounts.assignError', 'No se pudo asignar el empleado'));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRetryEmployee = async (employeeId: number) => {
    try {
      setRetryingId(employeeId);
      await apiService.retryClientAccountEmployee(accountId, employeeId);
      toast.success(t('clientAccounts.retryStarted', 'Reintento lanzado'));
      loadAccount();
    } catch (err: any) {
      console.error('Failed to retry employee:', err);
      toast.error(err.detail || t('clientAccounts.retryError', 'No se pudo reintentar el despliegue'));
    } finally {
      setRetryingId(null);
    }
  };

  // Cupo de empleados: los «removed» no ocupan plaza. El backend es la autoridad
  // (409 en la asignación si se supera); aquí solo pintamos y prevenimos en la UI.
  const usedSlots = account ? account.employees.filter((e) => e.status !== 'removed').length : 0;
  const maxEmployees = typeof account?.max_employees === 'number' ? account.max_employees : null;
  const quotaFull = maxEmployees !== null && usedSlots >= maxEmployees;
  const quotaValid = /^\d+$/.test(quotaValue.trim()) && Number(quotaValue) >= 1;

  // ── Almacenamiento ───────────────────────────────────────────────────────
  // El bloque `storage` lo calcula el backend (suma de los volúmenes de los
  // empleados vivos, incluido, facturable y cargo por ciclo): el admin ve
  // exactamente lo mismo que el cliente, no una estimación paralela.
  const storage = account?.storage ?? null;
  const storageStep = storage?.step_gib ?? 20;
  const includedValid = /^\d+$/.test(includedValue.trim());
  const maxStorageValid = /^\d+$/.test(maxStorageValue.trim());

  // Previsualización de lo que HARÍAN esos dos números sobre el uso de HOY: qué
  // pasa a ser facturable y cuánto costará por ciclo. Sin esto el admin fija un
  // incluido a ciegas y el cargo aparece en el ledger del cliente sin que nadie
  // lo haya visto venir. Todo con los mismos datos derivados del backend.
  const storagePrice = toNum(storage?.credits_per_gib_cycle) ?? 0;
  // Cuenta de cartera: el disco se le factura al PARTNER, no a ella (el backend
  // manda precio 0 y esta bandera). Se lee sin exigir que el tipo la declare —
  // sin ella, «precio 0» se leería como «no se cobra a nadie», que es falso.
  const storageBilledToPartner = Boolean(
    (storage as { billed_to_partner?: boolean } | null)?.billed_to_partner);
  const storageUsed = storage?.used_gib ?? 0;
  const previewIncluded = includedValid ? Number(includedValue) : (storage?.included_gib ?? 0);
  const previewMax = maxStorageValid ? Number(maxStorageValue) : (storage?.max_gib ?? 0);
  const previewBillable = Math.max(0, storageUsed - previewIncluded);
  const previewCredits = storagePrice > 0 ? previewBillable * storagePrice : 0;
  // Techo por debajo de lo ya ocupado: legal (no borra nada) pero hay que
  // decirlo — a partir de ahí el cliente no puede contratar ni ampliar.
  const previewCapBelowUse = previewMax > 0 && storageUsed > previewMax;

  const openStorageDialog = () => {
    setIncludedValue(String(storage?.included_gib ?? account?.included_storage_gib ?? 20));
    setMaxStorageValue(String(storage?.max_gib ?? account?.max_storage_gib ?? 0));
    setStorageDialogOpen(true);
  };

  const handleUpdateStorageConfig = async () => {
    if (!includedValid || !maxStorageValid) return;
    try {
      setStorageLoading(true);
      await apiService.updateClientAccount(accountId, {
        included_storage_gib: Number(includedValue),
        max_storage_gib: Number(maxStorageValue)
      });
      toast.success(t('clientAccounts.storageUpdated', 'Almacenamiento de la cuenta actualizado'));
      setStorageDialogOpen(false);
      loadAccount();
    } catch (err: any) {
      console.error('Failed to update storage config:', err);
      toast.error(err.detail || t('clientAccounts.storageError', 'No se pudo actualizar el almacenamiento'));
    } finally {
      setStorageLoading(false);
    }
  };

  // Ampliación del disco de un empleado: +1 tramo repartido a partes iguales
  // entre sus dos volúmenes. Irreversible y con cargo por ciclo mientras el
  // empleado exista — el diálogo lo dice antes de confirmar.
  const handleExpandEmployeeStorage = async () => {
    if (!expandTarget) return;
    const ws = Number(expandTarget.workspace_gib ?? 10);
    const data = Number(expandTarget.data_gib ?? 10);
    try {
      setExpandLoading(true);
      setExpandError(null);
      const res = await apiService.expandClientAccountEmployeeStorage(accountId, expandTarget.id, {
        workspace_gib: ws + Math.ceil(storageStep / 2),
        data_gib: data + Math.floor(storageStep / 2)
      });
      // `sync` = lo que pasó con el DISCO REAL en el tenant. Un 2xx solo dice
      // que la petición era válida; si el tenant no la aplicó hay que decirlo
      // (y en el caso 'failed' no se ha apuntado nada, así que el diálogo se
      // queda abierto para reintentar).
      const feedback = storageSyncFeedback(res?.sync, t);
      if (feedback.failed) {
        setExpandError(feedback.message);
        loadAccount();
        return;
      }
      setExpandTarget(null);
      if (feedback.severity === 'warning') {
        toast.warning(feedback.message);
      } else {
        toast.success(feedback.message);
      }
      loadAccount();
    } catch (err: any) {
      console.error('Failed to expand employee storage:', err);
      setExpandError(err.detail || t('clientAccounts.storageError', 'No se pudo ampliar el almacenamiento'));
    } finally {
      setExpandLoading(false);
    }
  };

  const openQuotaDialog = () => {
    setQuotaValue(String(maxEmployees ?? 1));
    setQuotaDialogOpen(true);
  };

  const handleUpdateQuota = async () => {
    if (!quotaValid) return;
    try {
      setQuotaLoading(true);
      await apiService.updateClientAccount(accountId, { max_employees: Number(quotaValue) });
      toast.success(t('clientAccounts.quotaUpdated', 'Cupo de empleados actualizado'));
      setQuotaDialogOpen(false);
      loadAccount();
    } catch (err: any) {
      console.error('Failed to update employee quota:', err);
      toast.error(err.detail || t('clientAccounts.quotaError', 'No se pudo actualizar el cupo'));
    } finally {
      setQuotaLoading(false);
    }
  };

  // Desbloqueo del propietario: POST /client-accounts/{id}/unlock. Recarga el
  // detalle para que el panel refleje el estado real que devuelve el backend.
  const handleUnlockOwner = async () => {
    try {
      await apiService.unlockClientAccountOwner(accountId);
      toast.success(t('clientAccounts.unlockOwnerDone', 'Acceso desbloqueado'));
      await loadAccount();
    } catch (err: any) {
      console.error('Failed to unlock account owner:', err);
      toast.error(err.detail || t('clientAccounts.unlockOwnerError', 'No se pudo desbloquear el acceso'));
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetLoading(true);
      const result = await apiService.resetClientAccountPassword(accountId);
      setResetConfirmOpen(false);
      setResetInfo(result);
      setResetPasswordDialogOpen(true);
    } catch (err: any) {
      console.error('Failed to reset owner password:', err);
      toast.error(err.detail || t('clientAccounts.resetPasswordError', 'No se pudo resetear la contraseña'));
    } finally {
      setResetLoading(false);
    }
  };

  // Borrado físico: needsForce cuando quedan empleados sin retirar (el backend
  // exige force=true y aquí, además, escribir el nombre exacto de la cuenta).
  const needsForce = usedSlots > 0;
  const deleteConfirmed = !needsForce || deleteConfirmName.trim() === (account?.name || '');

  const openDeleteDialog = () => {
    setDeleteConfirmName('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmed) return;
    try {
      setDeleteLoading(true);
      const result = await apiService.deleteClientAccount(accountId, needsForce);
      // La cuenta local se borra SIEMPRE: los tres pasos contra el tenant
      // (retirar empleados, limpiar usuarios de plataforma, borrar el proyecto)
      // son best-effort, así que lo único que decide el tono del aviso es QUÉ
      // QUEDÓ VIVO allí. Se acumulan en UN solo toast: son restos del mismo
      // borrado y encadenar tres avisos los haría perderse.
      const leftovers: string[] = [];
      if (result.remove_failures && result.remove_failures.length > 0) {
        leftovers.push(
          t('clientAccounts.deleteLeftoverEmployees',
            '{{count}} empleado(s) no se pudieron retirar',
            { count: result.remove_failures.length })
        );
      }
      const cleanup = result.users_cleanup;
      if (cleanup?.error) {
        leftovers.push(
          t('clientAccounts.deleteLeftoverUsersError',
            'la limpieza de usuarios de plataforma falló ({{error}})',
            { error: cleanup.error })
        );
      } else if (cleanup?.kept && cleanup.kept.length > 0) {
        // `kept` ya viene filtrado por el backend: son los que SIGUEN VIVOS en
        // el tenant y su motivo (superusuario, acceso a otros proyectos, error…).
        leftovers.push(
          t('clientAccounts.deleteLeftoverUsersKept',
            '{{count}} usuario(s) de plataforma siguen en el tenant ({{detail}})',
            {
              count: cleanup.kept.length,
              detail: cleanup.kept.map((k) => `${k.email}: ${k.reason}`).join(' · ')
            })
        );
      }
      if (result.project_error) {
        leftovers.push(
          t('clientAccounts.deleteLeftoverProject',
            'el proyecto del tenant NO se borró ({{error}})',
            { error: result.project_error })
        );
      }
      if (leftovers.length > 0) {
        toast.warning(
          t('clientAccounts.deleteWithFailures',
            'Cuenta eliminada, pero quedó algo vivo en el tenant global: {{detail}}',
            { detail: leftovers.join('; ') })
        );
      } else {
        toast.success(t('clientAccounts.deleted', 'Cuenta eliminada'));
      }
      if (result.owner_kept_reason) {
        toast.info(result.owner_kept_reason);
      }
      navigate('/client-accounts');
    } catch (err: any) {
      console.error('Failed to delete client account:', err);
      toast.error(err.detail || t('clientAccounts.deleteError', 'No se pudo eliminar la cuenta'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSyncConsumption = async () => {
    try {
      setSyncLoading(true);
      const result = await apiService.syncClientAccountConsumption(accountId);
      toast.success(
        result.entry_written
          ? t('clientAccounts.syncDone', 'Consumo sincronizado. Saldo actual: {{balance}}', {
              balance: formatCredits(result.balance)
            })
          : t('clientAccounts.syncNoChanges', 'Sin consumo nuevo que registrar')
      );
      loadAccount();
    } catch (err: any) {
      console.error('Failed to sync consumption:', err);
      toast.error(err.detail || t('clientAccounts.syncError', 'No se pudo sincronizar el consumo'));
    } finally {
      setSyncLoading(false);
    }
  };

  const creditKindLabel = (entry: CreditEntry) => {
    switch (entry.kind) {
      case 'topup':
        return t('clientAccounts.creditKind.topup', 'Recarga');
      case 'adjustment':
        return t('clientAccounts.creditKind.adjustment', 'Ajuste');
      case 'consumption':
        return t('clientAccounts.creditKind.consumption', 'Consumo');
      default:
        return entry.kind;
    }
  };

  const employeeName = (employeeId: number | null): string => {
    if (employeeId === null || !account) return '—';
    const emp = account.employees.find((e) => e.id === employeeId);
    return emp ? (emp.display_name || emp.listing_slug) : `#${employeeId}`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/client-accounts')} sx={{ mb: 2 }}>
          {t('common.back', 'Atrás')}
        </Button>
        <Alert severity="error">
          {error || t('clientAccounts.notFound', 'Cuenta no encontrada')}
        </Alert>
      </Box>
    );
  }

  // ── Plan y créditos (tarjeta prominente) ──────────────────────────────────
  // El resumen «dos bolsas» viene ANIDADO en credit_summary (calculado sobre el
  // ledger por el backend); el spread tolera un backend que promocione los
  // campos a top-level. plan_credits es el N/mes CONTRATADO (lo que edita el
  // admin) — puede diferir del grant del ciclo vigente si cambió a mitad de
  // ciclo. Sin datos nuevos todo degrada a «—» + saldo simple, sin romper.
  const summary: CreditPlanFields = { ...account, ...(account.credit_summary ?? {}) };
  const planContractedNum = toNum(account.plan_credits) ?? toNum(summary.plan_total);
  const hasPlan = planContractedNum !== null && planContractedNum > 0;
  const planTotalNum = toNum(summary.plan_total);
  const planUsedNum = toNum(summary.plan_used);
  const extraNum = toNum(summary.extra_balance);
  const availableTotalNum = toNum(summary.available_total);
  const renewLabel = summary.plan_renews_at ? formatRenewDate(summary.plan_renews_at) : null;
  const planPct = planTotalNum !== null && planTotalNum > 0
    ? Math.min(100, Math.max(0, ((planUsedNum ?? 0) / planTotalNum) * 100))
    : 0;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/client-accounts')}>
            {t('common.back', 'Atrás')}
          </Button>
          <Typography variant="h4" component="h1">
            {account.name}
          </Typography>
          <Chip
            label={account.status === 'active' ? t('clientAccounts.status.active', 'Activa') : account.status}
            color={account.status === 'active' ? 'success' : 'default'}
            size="small"
          />
        </Box>
        {/* Las acciones de plan/créditos viven DENTRO de la tarjeta «Plan y
            créditos» (junto a los datos que tocan); aquí quedan las de cuenta. */}
        <Box display="flex" gap={2}>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => loadAccount()}>
            {t('common.refresh', 'Actualizar')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<LockReset />}
            onClick={() => setResetConfirmOpen(true)}
          >
            {t('clientAccounts.resetPassword', 'Resetear contraseña')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteForever />}
            onClick={openDeleteDialog}
          >
            {t('clientAccounts.delete', 'Eliminar')}
          </Button>
        </Box>
      </Box>

      {/* Datos + plan/créditos */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('clientAccounts.accountData', 'Datos de la cuenta')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.fields.owner', 'Propietario')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {account.owner_email || '—'}
                  </Typography>
                  {/* Contrato F1: por qué el cliente no puede entrar (bloqueo
                      por intentos, contraseña temporal sin cambiar) + el
                      desbloqueo, que NO toca su contraseña. */}
                  <PortalAccessPanel access={account.owner_access} onUnlock={handleUnlockOwner} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.fields.company', 'Empresa')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {account.company || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.fields.globalTenant', 'Tenant global')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {account.global_tenant_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.fields.createdAt', 'Creada')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {format(new Date(account.created_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Grid>
                {/* GESTIÓN de la cuenta: si la lleva un partner, quién y su
                    contacto con enlace a su ficha; si no, cuenta directa. Es la
                    misma distinción que el listado, aquí con el contacto. */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.management', 'Gestión de la cuenta')}
                  </Typography>
                  {account.reseller_id ? (
                    <Typography variant="body1" fontWeight={600}>
                      {t('clientAccounts.managedBy', 'Gestionada por')}{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={() => navigate(`/partners/${account.reseller_id}`)}
                        sx={{ verticalAlign: 'baseline' }}
                      >
                        {account.reseller_name || `#${account.reseller_id}`}
                      </Link>
                      {account.reseller_contact_email ? ` · ${account.reseller_contact_email}` : ''}
                    </Typography>
                  ) : (
                    <Typography variant="body1" fontWeight={600}>
                      {t('clientAccounts.directAccount', 'Cuenta directa')}
                    </Typography>
                  )}
                </Grid>
                {/* ALMACENAMIENTO agregado de la cuenta: usado / incluido sin
                    coste / techo, y el cargo por ciclo que sale del exceso.
                    Aquí y no en una pantalla aparte porque es una condición de
                    la cuenta, igual que el cupo de empleados. */}
                {storage && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StorageIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {t('clientAccounts.storage', 'Almacenamiento')}
                      </Typography>
                      <Tooltip title={t('clientAccounts.editStorage', 'Editar incluido y límite')}>
                        <IconButton size="small" onClick={openStorageDialog}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {t('clientAccounts.storageUsage', '{{used}} GiB usados · {{included}} incluidos', {
                        used: storage.used_gib,
                        included: storage.included_gib
                      })}
                      {storage.max_gib > 0
                        ? ' · ' + t('clientAccounts.storageMax', 'límite {{max}} GiB', { max: storage.max_gib })
                        : ' · ' + t('clientAccounts.storageNoMax', 'sin límite')}
                    </Typography>
                    {storage.billable_gib > 0 && (
                      <Typography variant="caption" color="warning.main" display="block">
                        {t('clientAccounts.storageBillable', '{{gib}} GiB facturables · {{credits}} créditos/ciclo', {
                          gib: storage.billable_gib,
                          credits: formatCredits(storage.cycle_credits)
                        })}
                      </Typography>
                    )}
                  </Grid>
                )}
                {account.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('clientAccounts.fields.notes', 'Notas')}
                    </Typography>
                    <Typography variant="body2">{account.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* Tarjeta prominente «Plan y créditos»: de un vistazo el plan
            contratado, cuándo (y cuánto) se recarga, el disponible destacado,
            el consumido del ciclo y la bolsa extra — con sus acciones al pie. */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6">{t('clientAccounts.planCreditsTitle', 'Plan y créditos')}</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('creditsSummary.availableTotal', 'Disponible total')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {formatCredits(availableTotalNum ?? account.balance)}
                  </Typography>
                  {/* SIN equivalencia en dólares: este panel es de CONSUMO
                      (saldo que se drena con el uso real), no de precio.
                      Mezclar aquí lo que cuesta comprar un crédito
                      (CREDIT_PRICE_USD) con lo que representa de consumo
                      (CREDIT_USD_RATE) hacía leer el saldo como una factura. */}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('creditsSummary.creditsUnit', 'créditos')}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.planContracted', 'Plan contratado')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {hasPlan
                      ? t('clientAccounts.planPerMonth', '{{credits}}/mes', {
                          credits: formatCredits(planContractedNum)
                        })
                      : t('clientAccounts.noPlan', 'Sin plan')}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.planRenewsAt', 'Próxima renovación')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {renewLabel ?? '—'}
                  </Typography>
                  {renewLabel && hasPlan && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('clientAccounts.planRenewGrant', 'se recargan {{credits}} créditos', {
                        credits: formatCredits(planContractedNum)
                      })}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.consumedCycle', 'Consumido este ciclo')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {planUsedNum !== null ? formatCredits(planUsedNum) : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('clientAccounts.extraNoExpiry', 'Extra (sin caducidad)')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {extraNum !== null ? formatCredits(extraNum) : '—'}
                  </Typography>
                </Grid>
                {planTotalNum !== null && planTotalNum > 0 && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('creditsSummary.plan', 'Plan mensual')}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={planPct}
                      sx={{ height: 8, borderRadius: 4, my: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t('creditsSummary.planUsage', '{{used}} de {{total}} usados', {
                        used: formatCredits(planUsedNum ?? 0),
                        total: formatCredits(planTotalNum)
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Box display="flex" gap={1.5} mt={2.5} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EventRepeat />}
                  onClick={openPlanDialog}
                >
                  {t('clientAccounts.assignPlan', 'Asignar plan')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AccountBalanceWallet />}
                  onClick={() => setCreditsDialogOpen(true)}
                >
                  {t('clientAccounts.addExtraCredits', 'Añadir créditos extra')}
                </Button>
                <LoadingButton
                  variant="outlined"
                  size="small"
                  startIcon={<Sync />}
                  loading={syncLoading}
                  onClick={handleSyncConsumption}
                >
                  {t('clientAccounts.syncConsumption', 'Sincronizar consumo')}
                </LoadingButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Empleados */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <SmartToy color="primary" />
              <Typography variant="h6">
                {t('clientAccounts.employees', 'Empleados IA')}{' '}
                ({maxEmployees !== null ? `${usedSlots}/${maxEmployees}` : account.employees.length})
              </Typography>
              <Tooltip title={t('clientAccounts.editQuota', 'Editar cupo de empleados')}>
                <IconButton size="small" onClick={openQuotaDialog}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Tooltip
              title={quotaFull
                ? t('clientAccounts.quotaFullHint', 'Cupo completo: amplía «Máx. empleados» para asignar más')
                : ''}
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={openAssignDialog}
                  disabled={quotaFull}
                >
                  {t('clientAccounts.assignEmployee', 'Asignar empleado')}
                </Button>
              </span>
            </Tooltip>
          </Box>
          {account.employees.length === 0 ? (
            <Typography color="text.secondary" align="center" py={2}>
              {t('clientAccounts.noEmployees', 'Esta cuenta todavía no tiene empleados asignados.')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('clientAccounts.fields.employee', 'Empleado')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.listing', 'Listing')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.admin', 'Administrador')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.status', 'Estado')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.app', 'Aplicación')}</TableCell>
                    <TableCell align="right">{t('clientAccounts.fields.allocatedCredits', 'Límite mensual')}</TableCell>
                    <TableCell align="right">{t('clientAccounts.fields.storage', 'Disco')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.createdAt', 'Creada')}</TableCell>
                    <TableCell align="right">{t('clientAccounts.fields.actions', 'Acciones')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.employees.map((emp) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {emp.display_name || emp.listing_slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {emp.listing_slug}
                          {emp.listing_version ? ` · v${emp.listing_version}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* Empleados anteriores al modelo de administrador: sin
                            dato. Se dice explícitamente en vez de dejar el hueco. */}
                        {emp.admin_user_name ? (
                          <Tooltip title={emp.admin_user_email || ''}>
                            <Typography variant="body2">{emp.admin_user_name}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('clientAccounts.noAdminDeclared', 'Sin administrador declarado')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmployeeStatusChip status={emp.status} />
                          {emp.status === 'failed' && emp.error_message && (
                            <Tooltip title={emp.error_message}>
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  maxWidth: 260,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'inline-block'
                                }}
                              >
                                {emp.error_message}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {emp.status === 'active' && emp.app_url ? (
                          <Link
                            href={emp.app_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            {t('clientAccounts.openApp', 'Abrir app')}
                            <OpenInNew fontSize="inherit" />
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {emp.allocated_credits === null || emp.allocated_credits === undefined ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          <Typography variant="body2" fontWeight={600}>
                            {formatCredits(emp.allocated_credits)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {/* Los DOS volúmenes del empleado y su suma: es lo que
                            ocupa de la cuenta desde el primer día. */}
                        <Tooltip
                          title={t('clientAccounts.storageBreakdown', '{{ws}} GiB espacio de trabajo + {{data}} GiB datos', {
                            ws: emp.workspace_gib ?? 10,
                            data: emp.data_gib ?? 10
                          })}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {emp.storage_gib ?? Number(emp.workspace_gib ?? 10) + Number(emp.data_gib ?? 10)} GiB
                          </Typography>
                        </Tooltip>
                        {/* CAPACIDAD desplegada — la otra mitad de «con qué
                            recursos trabaja». Cambiarla exige redesplegar. */}
                        <Box sx={{ mt: 0.5 }}>
                          <EmployeeSizeChip size={emp.size} />
                        </Box>
                      </TableCell>
                      <TableCell>{format(new Date(emp.created_at), 'dd/MM/yyyy')}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          {(emp.status === 'failed' || emp.status === 'pending') && (
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<Replay />}
                              loading={retryingId === emp.id}
                              onClick={() => handleRetryEmployee(emp.id)}
                            >
                              {t('clientAccounts.retry', 'Reintentar')}
                            </LoadingButton>
                          )}
                          {emp.status !== 'removed' && (
                            <Tooltip
                              title={t('clientAccounts.expandStorageHint', 'Ampliar +{{step}} GiB (permanente, genera cargo por ciclo)', {
                                step: storageStep
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setExpandTarget(emp);
                                  setExpandError(null);
                                }}
                              >
                                <StorageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Usuarios */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <People color="primary" />
            <Typography variant="h6">
              {t('clientAccounts.users', 'Usuarios')} ({account.users.length})
            </Typography>
          </Box>
          {account.users.length === 0 ? (
            <Typography color="text.secondary" align="center" py={2}>
              {t('clientAccounts.noUsers', 'Esta cuenta todavía no tiene usuarios. El propietario puede añadirlos desde su portal.')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('clientAccounts.fields.email', 'Email')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.fullName', 'Nombre')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.status', 'Estado')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.createdAt', 'Creada')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>
                        {/* Estado REAL en el tenant si el backend lo trae
                            («Pendiente de crear contraseña» / «Activo»); si no,
                            el estado local de la fila. */}
                        <AccountUserStatusChip user={user} />
                      </TableCell>
                      <TableCell>{format(new Date(user.created_at), 'dd/MM/yyyy')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Ledger de créditos */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AccountBalanceWallet color="primary" />
            <Typography variant="h6">{t('clientAccounts.creditLedger', 'Movimientos de créditos')}</Typography>
          </Box>
          {account.credit_entries.length === 0 ? (
            <Typography color="text.secondary" align="center" py={2}>
              {t('clientAccounts.noCreditEntries', 'Sin movimientos todavía.')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.date', 'Fecha')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.kind', 'Tipo')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.concept', 'Concepto')}</TableCell>
                    <TableCell>{t('clientAccounts.fields.employee', 'Empleado')}</TableCell>
                    {/* CRÉDITOS, no dinero: el ledger de una cuenta se mide en créditos y
                        rotularlo «Importe» hacía leer dólares donde no los hay. */}
                    <TableCell align="right">{t('clientAccounts.fields.credits', 'Créditos')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {account.credit_entries.map((entry) => {
                    const amount = Number(entry.amount);
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>{format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                        <TableCell>
                          <Chip
                            label={creditKindLabel(entry)}
                            size="small"
                            color={entry.kind === 'topup' ? 'success' : entry.kind === 'consumption' ? 'default' : 'info'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{entry.concept || '—'}</TableCell>
                        <TableCell>{employeeName(entry.account_employee_id)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={amount < 0 ? 'error.main' : 'success.main'}
                          >
                            {amount > 0 ? '+' : ''}{formatCredits(entry.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo añadir créditos EXTRA (topup — no toca el plan mensual) */}
      <Dialog open={creditsDialogOpen} onClose={() => setCreditsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('clientAccounts.addExtraCredits', 'Añadir créditos extra')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('clientAccounts.fields.credits', 'Créditos') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={creditsAmount}
            onChange={(e) => setCreditsAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={creditsAmount !== '' && Number(creditsAmount) <= 0}
            helperText={creditsAmount !== '' && Number(creditsAmount) <= 0
              ? t('clientAccounts.amountPositive', 'El importe debe ser mayor que cero')
              : undefined}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.concept', 'Concepto') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={creditsConcept}
            onChange={(e) => setCreditsConcept(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditsDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleAddCredits}
            loading={creditsLoading}
            disabled={!creditsAmount || Number(creditsAmount) <= 0}
            variant="contained"
          >
            {t('clientAccounts.addExtraCredits', 'Añadir créditos extra')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo asignar plan mensual — POST /client-accounts/{id}/plan */}
      <Dialog open={planDialogOpen} onClose={() => !planLoading && setPlanDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('clientAccounts.assignPlan', 'Asignar plan')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('clientAccounts.planHint', 'El plan mensual es independiente de los créditos extra: se renueva cada ciclo con los créditos indicados.')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('clientAccounts.fields.planCredits', 'Créditos al mes') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={planCredits}
            onChange={(e) => setPlanCredits(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={planCredits !== '' && !planCreditsValid}
            helperText={planCredits !== '' && !planCreditsValid
              ? t('clientAccounts.planCreditsInvalid', 'Debe ser un número mayor que cero')
              : undefined}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.cycleDay', 'Día de ciclo (1–28)') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={planCycleDay}
            onChange={(e) => setPlanCycleDay(e.target.value)}
            inputProps={{ min: 1, max: 28, step: 1 }}
            error={!planCycleDayValid}
            helperText={!planCycleDayValid
              ? t('clientAccounts.cycleDayInvalid', 'Debe ser un entero entre 1 y 28')
              : t('clientAccounts.fields.cycleDayHelp', 'Día del mes en que se renueva el plan')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)} disabled={planLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleAssignPlan}
            loading={planLoading}
            disabled={!planCreditsValid || !planCycleDayValid}
            variant="contained"
          >
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo editar cupo de empleados — PATCH max_employees */}
      <Dialog open={quotaDialogOpen} onClose={() => setQuotaDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('clientAccounts.editQuota', 'Editar cupo de empleados')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('clientAccounts.quotaCurrent', 'Empleados actuales: {{used}}', { used: usedSlots })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('clientAccounts.fields.maxEmployees', 'Máx. empleados') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={quotaValue}
            onChange={(e) => setQuotaValue(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            error={!quotaValid}
            helperText={!quotaValid
              ? t('clientAccounts.maxEmployeesInvalid', 'Debe ser un entero mayor o igual que 1')
              : t('clientAccounts.fields.maxEmployeesHelp', 'Cuántos empleados IA puede contratar esta cuenta')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotaDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleUpdateQuota}
            loading={quotaLoading}
            disabled={!quotaValid}
            variant="contained"
          >
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo almacenamiento de la CUENTA — PATCH included/max storage */}
      <Dialog open={storageDialogOpen} onClose={() => setStorageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('clientAccounts.editStorage', 'Editar incluido y límite')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('clientAccounts.storageCurrent', 'Uso actual: {{used}} GiB en {{employees}} empleado(s)', {
              used: storage?.used_gib ?? 0,
              employees: usedSlots
            })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('clientAccounts.fields.includedStorage', 'GiB incluidos sin coste') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={includedValue}
            onChange={(e) => setIncludedValue(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!includedValid}
            helperText={!includedValid
              ? t('clientAccounts.storageIntegerInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('clientAccounts.fields.includedStorageHelp', 'Con {{step}} GiB, el primer empleado no genera cargo', { step: storageStep })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.maxStorage', 'Límite de almacenamiento (GiB)') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={maxStorageValue}
            onChange={(e) => setMaxStorageValue(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!maxStorageValid}
            helperText={!maxStorageValid
              ? t('clientAccounts.storageIntegerInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('clientAccounts.fields.maxStorageHelp', '0 = sin límite. Bajarlo por debajo de lo ya usado no borra nada: solo frena ampliaciones y contrataciones futuras')}
          />
          {/* Efecto de esos dos números sobre el uso de HOY: es la única forma
              de fijar el incluido sabiendo qué se le empieza a cobrar. */}
          <Alert severity={previewCapBelowUse ? 'warning' : 'info'} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {storageBilledToPartner
                ? t('clientAccounts.storagePreviewPartner',
                    'Con {{included}} GiB incluidos, hoy quedarían {{billable}} GiB por encima de lo incluido. Esta cuenta la gestiona un partner: ese exceso se le factura a él, no a la cuenta.',
                    { included: previewIncluded, billable: previewBillable })
                : storagePrice <= 0
                ? t('clientAccounts.storagePreviewFree',
                    'Con {{included}} GiB incluidos, hoy quedarían {{billable}} GiB por encima de lo incluido. El almacenamiento no tiene precio en esta plataforma: sin coste.',
                    { included: previewIncluded, billable: previewBillable })
                : previewBillable > 0
                  ? t('clientAccounts.storagePreviewCost',
                      'Con {{included}} GiB incluidos, hoy se facturarían {{billable}} GiB: {{credits}} créditos por ciclo con cargo a esta cuenta.',
                      {
                        included: previewIncluded,
                        billable: previewBillable,
                        credits: formatCredits(previewCredits)
                      })
                  : t('clientAccounts.storagePreviewCovered',
                      'Con {{included}} GiB incluidos, los {{used}} GiB que usa hoy la cuenta caben enteros: sin cargo de almacenamiento.',
                      { included: previewIncluded, used: storageUsed })}
            </Typography>
            {previewCapBelowUse && (
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {t('clientAccounts.storagePreviewCapBelow',
                  'El límite ({{max}} GiB) queda por debajo de lo que ya usa ({{used}} GiB): no se borra nada, pero la cuenta no podrá contratar ni ampliar hasta que baje del límite.',
                  { max: previewMax, used: storageUsed })}
              </Typography>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStorageDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleUpdateStorageConfig}
            loading={storageLoading}
            disabled={!includedValid || !maxStorageValid}
            variant="contained"
          >
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo AMPLIAR el disco de un empleado — irreversible y con cargo */}
      <Dialog
        open={expandTarget !== null}
        onClose={() => !expandLoading && setExpandTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('clientAccounts.expandStorage', 'Ampliar almacenamiento del empleado')}</DialogTitle>
        <DialogContent>
          {expandError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExpandError(null)}>
              {expandError}
            </Alert>
          )}
          {expandTarget && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('clientAccounts.fields.employee', 'Empleado')}:{' '}
                <strong>{expandTarget.display_name || expandTarget.listing_slug}</strong>
                {' · '}
                {t('clientAccounts.storageFromTo', '{{from}} → {{to}} GiB', {
                  from: Number(expandTarget.workspace_gib ?? 10) + Number(expandTarget.data_gib ?? 10),
                  to: Number(expandTarget.workspace_gib ?? 10) + Number(expandTarget.data_gib ?? 10) + storageStep
                })}
              </Typography>
              <Alert severity="warning">
                <AlertTitle>{t('clientAccounts.storageWarningTitle', 'Es permanente')}</AlertTitle>
                <Typography variant="body2">
                  {t('clientAccounts.storageWarningPermanent',
                    'El disco no se puede reducir después (encogerlo obligaría a recrear el volumen perdiendo los datos). Solo retirar el empleado libera el espacio, y el tamaño nuevo se aplica al reiniciar su aplicación.')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('clientAccounts.storageWarningCostAdmin',
                    'Los GiB que superen lo incluido en la cuenta generan cargo por ciclo mientras el empleado exista.')}
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandTarget(null)} disabled={expandLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleExpandEmployeeStorage}
            loading={expandLoading}
            variant="contained"
            color="warning"
          >
            {t('clientAccounts.expandStorageConfirm', 'Ampliar +{{step}} GiB', { step: storageStep })}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo asignar empleado — selector por NOMBRE, el picker rellena el slug */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clientAccounts.assignEmployee', 'Asignar empleado')}</DialogTitle>
        <DialogContent>
          {/* Sin usuarios en la cuenta NO se puede asignar: el empleado necesita
              un administrador real (es su identidad de trabajo). El admin del
              marketplace no da de alta usuarios de cliente — lo hace el cliente
              desde su portal —, así que el aviso dice exactamente eso. */}
          {adminCandidates.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
              {t('clientAccounts.noAdminCandidates',
                'Esta cuenta todavía no tiene usuarios. El cliente debe añadir al menos uno desde su portal («Usuarios»): ese usuario será el administrador del empleado y la identidad con la que trabajará.')}
            </Alert>
          )}
          {/* El selector solo da nombres: el catálogo público es donde se ve
              qué hace cada empleado. Va JUNTO al picker, antes de elegir. */}
          <EmployeeCatalogLink sx={{ mt: 1, mb: 1 }} />
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('clientAccounts.fields.listingPicker', 'Empleado del catálogo')} *</InputLabel>
            <Select
              value={selectedListingSlug}
              onChange={(e) => setSelectedListingSlug(e.target.value as string)}
              label={t('clientAccounts.fields.listingPicker', 'Empleado del catálogo') + ' *'}
              disabled={loadingListings}
            >
              <MenuItem value="" disabled>
                {loadingListings
                  ? t('common.loading', 'Cargando...')
                  : t('clientAccounts.selectListing', 'Selecciona un empleado')}
              </MenuItem>
              {listingOptions.map((option) => (
                <MenuItem key={option.slug} value={option.slug}>
                  {option.display_name || option.slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Administrador del empleado — selector por NOMBRE (rellena el id) */}
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('clientAccounts.fields.employeeAdmin', 'Administrador del empleado')} *</InputLabel>
            <Select
              value={assignAdminUserId === '' ? '' : String(assignAdminUserId)}
              onChange={(e) => setAssignAdminUserId(Number(e.target.value))}
              label={t('clientAccounts.fields.employeeAdmin', 'Administrador del empleado') + ' *'}
              disabled={adminCandidates.length === 0}
            >
              <MenuItem value="" disabled>
                {t('clientAccounts.selectAdmin', 'Selecciona un usuario de la cuenta')}
              </MenuItem>
              {adminCandidates.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.full_name} · {u.email}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('clientAccounts.employeeAdminHelp',
                'Administrará el empleado y será la identidad con la que trabaje (usuario de plataforma).')}
            </FormHelperText>
          </FormControl>
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.displayName', 'Nombre para mostrar') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={assignDisplayName}
            onChange={(e) => setAssignDisplayName(e.target.value)}
            helperText={t('clientAccounts.displayNameHelp', 'Si lo dejas vacío se usará el nombre del catálogo')}
            sx={{ mb: 2 }}
          />
          {/* ALMACENAMIENTO del empleado. capBlocks=false porque el camino del
              admin NO aplica el techo de la cuenta (assign_employee solo valida
              la FORMA del par): aquí se avisa, no se bloquea — como el backend. */}
          <EmployeeStoragePicker
            storage={storage}
            value={assignVolumes}
            onChange={setAssignVolumes}
            disabled={assignLoading}
            capBlocks={false}
          />
          {/* CAPACIDAD del empleado. Va después del disco a propósito: el disco
              tiene coste y techo (hay que decidirlo mirando la cuenta), la
              capacidad no — es una decisión sobre cómo va a trabajar. */}
          <EmployeeSizePicker
            value={assignSize}
            onChange={setAssignSize}
            disabled={assignLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleAssignEmployee}
            loading={assignLoading}
            disabled={!selectedListingSlug || assignAdminUserId === ''}
            variant="contained"
          >
            {t('clientAccounts.assign', 'Asignar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Eliminar cuenta — DELETE /client-accounts/{id}?force=... (borrado FÍSICO) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('clientAccounts.deleteTitle', '¿Eliminar esta cuenta?')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t('clientAccounts.deleteIrreversibleTitle', 'Esta acción es IRREVERSIBLE')}</AlertTitle>
            {t(
              'clientAccounts.deleteConsequences',
              'Se retiran sus empleados IA del tenant (las apps se desinstalan y sus datos se purgan) y se borra físicamente TODA la cuenta: usuarios, empleados y también el historial de créditos. No hay papelera ni recuperación posible.'
            )}
          </Alert>
          {/* Lo que se borra AL OTRO LADO (tenant global). No es un detalle
              técnico: el proyecto es el ancla de la cuenta allí y los usuarios
              de plataforma pueden ser compartidos con otras cuentas, así que el
              admin debe saber a quién pierde de verdad y a quién solo desvincula. */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'clientAccounts.deleteTenantConsequences',
              'En el tenant global también se borra el PROYECTO de la cuenta, y sus usuarios de plataforma se limpian: los que solo pertenecían a esta cuenta se ELIMINAN; los que además trabajan en otros proyectos solo se desvinculan de este (conservan su acceso al resto).'
            )}
          </Alert>
          {account.owner_email && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: needsForce ? 2 : 0 }}>
              {t(
                'clientAccounts.deleteOwnerNote',
                'El propietario ({{email}}) solo se conserva si tiene suscripciones o transacciones de facturación; si no, se borra con la cuenta.',
                { email: account.owner_email }
              )}
            </Typography>
          )}
          {needsForce && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t(
                  'clientAccounts.deleteForceWarning',
                  'La cuenta tiene {{count}} empleado(s) desplegados: para confirmar el borrado forzado escribe el nombre exacto de la cuenta.',
                  { count: usedSlots }
                )}
              </Alert>
              <TextField
                autoFocus
                margin="dense"
                label={t('clientAccounts.deleteConfirmLabel', 'Escribe «{{name}}» para confirmar', { name: account.name })}
                fullWidth
                variant="outlined"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                error={deleteConfirmName !== '' && !deleteConfirmed}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleDeleteAccount}
            loading={deleteLoading}
            disabled={!deleteConfirmed}
            variant="contained"
            color="error"
          >
            {t('clientAccounts.deleteConfirm', 'Eliminar definitivamente')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirmación de reset — POST /client-accounts/{id}/reset-password */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => !resetLoading && setResetConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('clientAccounts.resetPasswordTitle', '¿Resetear la contraseña del propietario?')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t(
              'clientAccounts.resetPasswordWarning',
              'Se generará una contraseña nueva para {{email}} y la actual dejará de valer inmediatamente. La nueva se mostrará UNA sola vez.',
              { email: account.owner_email || '—' }
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={resetLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleResetPassword}
            loading={resetLoading}
            variant="contained"
            color="warning"
          >
            {t('clientAccounts.resetPassword', 'Resetear contraseña')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Contraseña reseteada — se muestra UNA sola vez (mismo patrón que el alta) */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('clientAccounts.resetPasswordDoneTitle', 'Contraseña reseteada')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {resetInfo && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t('clientAccounts.passwordOnceTitle', 'Importante: guarda esta contraseña')}</AlertTitle>
                {t('clientAccounts.passwordOnceMessage', 'La contraseña no volverá a mostrarse. Cópiala y entrégasela al propietario de la cuenta.')}
                {/* Solo si el correo salió de verdad (envío fail-soft en backend). */}
                {resetInfo.email_sent && (
                  <Box sx={{ mt: 1 }}>
                    {t('clientAccounts.passwordAlsoEmailed', 'También se ha enviado por correo a {{email}}', {
                      email: resetInfo.email
                    })}
                  </Box>
                )}
              </Alert>
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('clientAccounts.fields.ownerEmail', 'Email del propietario')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {resetInfo.email}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('clientAccounts.fields.generatedPassword', 'Contraseña generada')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {resetInfo.generated_password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(resetInfo.generated_password);
                      toast.success(t('clientAccounts.passwordCopied', 'Contraseña copiada al portapapeles'));
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)} variant="contained">
            {t('clientAccounts.passwordSaved', 'He guardado la contraseña')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAccountDetailPage;
